import { useEffect, useState } from 'react'
import { Search, Shield, ShieldOff, UserPlus, X, Trash2, AlertTriangle } from 'lucide-react'
import { api } from '@/api/client'
import { useAuth } from '@/contexts/AuthContext'
import type { UserFlat } from './types'

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export default function UsersTab() {
  const { user: authUser } = useAuth()
  const [users, setUsers] = useState<UserFlat[] | null>(null)
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [busyUserId, setBusyUserId] = useState<number | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<UserFlat | null>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 300)
    return () => clearTimeout(id)
  }, [q])

  useEffect(() => {
    setUsers(null)
    api.superadmin.users(debouncedQ || undefined)
      .then((r) => setUsers(r.users))
      .catch((e) => setError(e.message))
  }, [debouncedQ, reloadKey])

  async function toggleRole(u: UserFlat) {
    const makeSuperadmin = u.Role !== 'superadmin'
    const action = makeSuperadmin ? 'zum Superadmin machen' : 'Superadmin-Rechte entziehen (auf "user" setzen)'
    if (!window.confirm(`${u.DisplayName} (${u.Email}) ${action}?`)) return
    setBusyUserId(u.Id)
    try {
      await api.superadmin.updateUserRole(u.Id, makeSuperadmin ? 'superadmin' : 'user')
      setReloadKey((k) => k + 1)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      setError(msg)
    } finally {
      setBusyUserId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name oder E-Mail suchen…"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fom-500"
          />
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="ml-auto px-4 py-2 bg-fom-500 hover:bg-fom-600 text-white text-sm rounded-lg flex items-center gap-2"
        >
          <UserPlus size={16} /> Superadmin hinzufügen
        </button>
      </div>

      {error && (
        <div className="p-4 rounded border border-red-200 bg-red-50 text-sm text-red-700 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700"><X size={16} /></button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">E-Mail</th>
              <th className="px-3 py-2 text-left">Rolle</th>
              <th className="px-3 py-2 text-left">Standort</th>
              <th
                className="px-3 py-2 text-left"
                title="Account-Status (nicht Login-Aktivität). Neu angelegte User stehen auf 'aktiv', auch wenn sie sich noch nie eingeloggt haben. 'gesperrt' = der Account wurde explizit deaktiviert."
              >
                Status
              </th>
              <th className="px-3 py-2 text-left">Letzter Login</th>
              <th className="px-3 py-2 text-left">Failed</th>
              <th className="px-3 py-2 text-left w-20">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {!users && <tr><td colSpan={8} className="px-3 py-4 text-sm text-gray-500">Lade…</td></tr>}
            {users && users.length === 0 && <tr><td colSpan={8} className="px-3 py-4 text-sm text-gray-500">Keine Benutzer gefunden.</td></tr>}
            {users?.map((u) => {
              const isSelf = authUser?.Id === u.Id
              const isSuper = u.Role === 'superadmin'
              return (
                <tr key={u.Id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 font-semibold text-gray-900">{u.DisplayName}</td>
                  <td className="px-3 py-2 text-gray-700">{u.Email}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      u.Role === 'superadmin' ? 'bg-amber-100 text-amber-800' :
                      u.Role === 'admin' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-200 text-gray-700'
                    }`}>{u.Role}</span>
                  </td>
                  <td className="px-3 py-2">
                    {u.standort_firma_ids && u.standort_firma_ids.length > 0 ? (
                      <span
                        className="inline-block rounded-full bg-fom-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-fom-700"
                        title={`Standort-Admin für ${u.standort_firma_ids.length} Firma(s)`}
                      >
                        Standort-Admin · {u.standort_firma_ids.length}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={u.IsActive ? 'text-emerald-700' : 'text-rose-600 font-semibold'}>
                      {u.IsActive ? 'aktiv' : 'gesperrt'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{formatDateTime(u.LastLogin)}</td>
                  <td className="px-3 py-2 text-gray-700">{u.FailedLoginCount}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        disabled={busyUserId === u.Id || (isSelf && isSuper)}
                        onClick={() => toggleRole(u)}
                        title={
                          isSelf && isSuper ? 'Eigene Rolle nicht herabstufbar' :
                          isSuper ? 'Superadmin-Rechte entziehen' : 'Zum Superadmin machen'
                        }
                        className={`p-1.5 rounded ${
                          isSuper ? 'text-amber-700 hover:bg-amber-50' : 'text-gray-500 hover:bg-gray-100'
                        } disabled:opacity-40 disabled:cursor-not-allowed bg-transparent border-none cursor-pointer`}
                      >
                        {isSuper ? <ShieldOff size={16} /> : <Shield size={16} />}
                      </button>
                      <button
                        disabled={busyUserId === u.Id || isSelf}
                        onClick={() => setDeleteTarget(u)}
                        title={isSelf ? 'Eigenen Account nicht löschbar' : 'Benutzer komplett löschen'}
                        className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed bg-transparent border-none cursor-pointer transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <AddSuperadminModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false)
            setReloadKey((k) => k + 1)
          }}
        />
      )}

      {deleteTarget && (
        <DeleteUserModal
          target={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onSuccess={() => {
            setDeleteTarget(null)
            setReloadKey((k) => k + 1)
          }}
        />
      )}
    </div>
  )
}

function DeleteUserModal({
  target,
  onClose,
  onSuccess,
}: {
  target: UserFlat
  onClose: () => void
  onSuccess: () => void
}) {
  const [confirmEmail, setConfirmEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{
    portal_user: boolean
    totp: boolean
    crm_contact: boolean
    crm_error: string | null
  } | null>(null)

  const canSubmit = confirmEmail.trim().toLowerCase() === (target.Email || '').toLowerCase()

  async function submit() {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const r = await api.superadmin.deleteUser(target.Id)
      setResult(r.deleted)
      if (r.deleted.crm_error) {
        setTimeout(onSuccess, 2000)
      } else {
        setTimeout(onSuccess, 1000)
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose() }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 bg-red-50/70 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-600" />
            Benutzer löschen
          </h3>
          <button onClick={onClose} disabled={submitting}
            className="text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer disabled:opacity-50">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-sm text-gray-700">
            <p className="font-semibold mb-2">{target.DisplayName}</p>
            <p className="text-gray-500 mb-3">{target.Email}</p>
            <p>Diese Aktion entfernt:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-gray-600">
              <li>Portal-Account (Rollen, Permissions, Token)</li>
              <li>Authenticator-Eintrag (TOTP)</li>
              <li>CRM-Kontakt wird deaktiviert (Audit-Spur bleibt)</li>
            </ul>
            <p className="mt-3 text-red-700 font-medium">Nicht rückgängig zu machen.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Tippe zur Bestätigung die E-Mail-Adresse:
            </label>
            <input
              type="email"
              autoFocus
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={target.Email}
              disabled={submitting || !!result}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            {confirmEmail && !canSubmit && !result && (
              <p className="text-xs text-red-600 mt-1">E-Mail stimmt nicht überein</p>
            )}
          </div>

          {error && (
            <div className="p-3 rounded border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>
          )}
          {result && (
            <div className={`p-3 rounded border text-sm ${
              result.crm_error ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-green-200 bg-green-50 text-green-800'
            }`}>
              <div>✓ Portal-Account gelöscht</div>
              <div>✓ Authenticator entfernt</div>
              <div>{result.crm_contact ? '✓ CRM-Kontakt deaktiviert' : `⚠ CRM-Deaktivierung fehlgeschlagen — manuell nachholen${result.crm_error ? `: ${result.crm_error}` : ''}`}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 bg-transparent border-none cursor-pointer"
            >
              Abbrechen
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit || submitting || !!result}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg flex items-center gap-2 border-none cursor-pointer"
            >
              <Trash2 size={14} />
              {submitting ? 'Wird gelöscht…' : 'Endgültig löschen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function AddSuperadminModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void
  onSuccess: (result: { id: number; action: string }) => void
}) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ id: number; action: string } | null>(null)

  async function submit(ev: React.FormEvent) {
    ev.preventDefault()
    setError(null)
    if (!email.trim()) {
      setError('E-Mail ist Pflicht.')
      return
    }
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
    if (!emailRx.test(email.trim())) {
      setError('Bitte eine gültige E-Mail-Adresse eingeben.')
      return
    }
    setSubmitting(true)
    try {
      const r = await api.superadmin.addSuperadmin({
        email: email.trim().toLowerCase(),
        displayName: displayName.trim() || undefined,
      })
      setResult(r)
      setTimeout(() => onSuccess(r), 1200)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const actionLabel = (a: string) =>
    a === 'created' ? 'Neuer Superadmin-Account angelegt' :
    a === 'promoted' ? 'Bestehender Benutzer auf Superadmin hochgestuft' :
    a === 'already' ? 'Ist bereits Superadmin' :
    'OK'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 bg-amber-50/50 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Shield size={18} className="text-amber-600" />
            Superadmin hinzufügen
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">E-Mail</label>
            <input
              type="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@bcw-gruppe.de"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fom-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Anzeigename (optional)</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="z. B. Max Mustermann"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fom-500"
            />
          </div>

          {error && <div className="p-3 rounded border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>}
          {result && (
            <div className="p-3 rounded border border-green-200 bg-green-50 text-sm text-green-800">
              ✓ {actionLabel(result.action)} (User-ID {result.id}).
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={submitting || !!result}
              className="px-4 py-2 bg-fom-500 hover:bg-fom-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg"
            >
              {submitting ? 'Wird angelegt…' : 'Als Superadmin anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
